export interface CaseLocationCountry {
  code: string;
  name: string;
}

export interface CaseLocationRegion {
  id: string;
  name: string;
  countryCodes?: string[];
}

export function getCountryCodesCoveredByRegions(
  regionNames: string[],
  regions: CaseLocationRegion[],
): Set<string> {
  const nameSet = new Set(regionNames);
  const codes = new Set<string>();
  for (const r of regions) {
    if (!nameSet.has(r.name)) continue;
    for (const c of r.countryCodes ?? []) {
      codes.add(c);
    }
  }
  return codes;
}

export function countryNamesForSelectedRegions(
  regionNames: string[],
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
): string[] {
  const covered = getCountryCodesCoveredByRegions(regionNames, regions);
  return countries.filter((c) => covered.has(c.code)).map((c) => c.name);
}

export function pruneCountriesToRegionCoverage(
  regionNames: string[],
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
  currentCountryNames: string[],
): string[] {
  const covered = getCountryCodesCoveredByRegions(regionNames, regions);
  return currentCountryNames.filter((name) => {
    const c = countries.find((x) => x.name === name);
    if (!c) return false;
    return covered.has(c.code);
  });
}

export function adjustCountriesAfterRegionToggle(
  toggledRegionName: string,
  regionNowSelected: boolean,
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
  regionNamesAfterToggle: string[],
  currentCountryNames: string[],
): string[] | undefined {
  const toggled = regions.find((r) => r.name === toggledRegionName);
  if (!toggled) return undefined;

  if (regionNowSelected) {
    const codes = new Set(toggled.countryCodes ?? []);
    const toAdd = countries.filter((c) => codes.has(c.code)).map((c) => c.name);
    const existing = new Set(currentCountryNames);
    const result = [...currentCountryNames];
    for (const name of toAdd) {
      if (!existing.has(name)) {
        existing.add(name);
        result.push(name);
      }
    }
    return result;
  }

  const deselectedCodes = new Set(toggled.countryCodes ?? []);
  const covered = getCountryCodesCoveredByRegions(
    regionNamesAfterToggle,
    regions,
  );
  return currentCountryNames.filter((name) => {
    const c = countries.find((x) => x.name === name);
    if (!c) return false;
    if (!deselectedCodes.has(c.code)) return true;
    return covered.has(c.code);
  });
}

export function adjustRegionsAfterCountryToggle(
  toggledCountryName: string,
  countryNowSelected: boolean,
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
  currentRegionNames: string[],
  currentCountryNames: string[],
): string[] | undefined {
  const country = countries.find((c) => c.name === toggledCountryName);
  if (!country) return undefined;

  if (countryNowSelected) {
    const regionsToAdd = regions
      .filter((r) => r.countryCodes?.includes(country.code))
      .map((r) => r.name);
    return [...new Set([...currentRegionNames, ...regionsToAdd])];
  }

  const selectedCountryCodes = new Set(
    countries
      .filter((c) => currentCountryNames.includes(c.name))
      .map((c) => c.code),
  );
  return currentRegionNames.filter((regionName) => {
    const region = regions.find((r) => r.name === regionName);
    if (!region?.countryCodes?.includes(country.code)) return true;
    return region.countryCodes.some((code) => selectedCountryCodes.has(code));
  });
}

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((x) => setA.has(x));
}

export function syncCountriesAfterRegionBulkSelection(
  selectedRegionNames: string[],
  enabledRegionNames: string[],
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
  currentCountryNames: string[],
): string[] {
  if (selectedRegionNames.length === 0) {
    return pruneCountriesToRegionCoverage(
      [],
      regions,
      countries,
      currentCountryNames,
    );
  }
  if (
    enabledRegionNames.length > 0 &&
    sameStringSet(selectedRegionNames, enabledRegionNames)
  ) {
    return countryNamesForSelectedRegions(
      selectedRegionNames,
      regions,
      countries,
    );
  }
  return currentCountryNames;
}

export function regionNamesCoveringCountrySelections(
  selectedCountryNames: string[],
  countries: CaseLocationCountry[],
  regions: CaseLocationRegion[],
): string[] {
  const codes = new Set(
    countries
      .filter((c) => selectedCountryNames.includes(c.name))
      .map((c) => c.code),
  );
  const result: string[] = [];
  const seen = new Set<string>();
  for (const r of regions) {
    if (seen.has(r.name)) continue;
    if ((r.countryCodes ?? []).some((code) => codes.has(code))) {
      result.push(r.name);
      seen.add(r.name);
    }
  }
  return result;
}

export function syncRegionsAfterCountryBulkSelection(
  selectedCountryNames: string[],
  enabledCountryNames: string[],
  regions: CaseLocationRegion[],
  countries: CaseLocationCountry[],
  currentRegionNames: string[],
): string[] {
  if (selectedCountryNames.length === 0) {
    return [];
  }
  if (
    enabledCountryNames.length > 0 &&
    sameStringSet(selectedCountryNames, enabledCountryNames)
  ) {
    return regionNamesCoveringCountrySelections(
      selectedCountryNames,
      countries,
      regions,
    );
  }
  return currentRegionNames;
}

export function partitionCountryNamesEnabledFirst(
  namesInStableOrder: string[],
  disabledNames: string[],
): string[] {
  const disabledSet = new Set(disabledNames);
  const enabled = namesInStableOrder.filter((n) => !disabledSet.has(n));
  const disabledOrdered = namesInStableOrder.filter((n) => disabledSet.has(n));
  return [...enabled, ...disabledOrdered];
}
